import React, {
  FunctionComponent,
  useState,
  useContext,
  useEffect,
} from "react";
import { HelpDrawerToggle } from "@cmsgov/ds-medicare-gov";
import { withRouter, RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import {
  initiateLogin,
  useTranslate,
  sendTealiumEvent,
  useQueryParams,
} from "../../helpers";
import { FormattedMessage } from "react-intl-phraseapp";
import { AppContext } from "../../app/store";
import { ActionType } from "../../@types";
import { LoginLogoutButton } from "../../components";
import { useFlags } from "launchdarkly-react-client-sdk";
import Header from "../../components/Header/Header";
import { HeroInfo } from "./HeroInfo";
import { getRoutes } from "./LandingPageHelpers";
import LandingPageMedigapHelpDrawer from "./LandingPageMedigapHelpDrawer";
import routes from "../../app/routes";
import { HashLink } from "react-router-hash-link";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";

const LandingPage: FunctionComponent<RouteComponentProps> = ({
  history,
  location,
  match,
}) => {
  const t = useTranslate();
  const [showMedigapHelp, setShowMedigapHelp] = useState(false);
  const { state, dispatch } = useContext(AppContext);
  const flags = useFlags();
  const { mctYpOeNextYear, mctCurrentPlanYear } = flags;
  useQueryParams({ history, location, match });

  const { beneRouteForThisYear, questionsRouteForThisYear } = getRoutes(
    mctCurrentPlanYear
  );

  useEffect(() => {
    return (): void =>
      dispatch({ type: ActionType.UPDATE_MBP_HANDOFF_FAILED, payload: false });
  }, []);

  return (
    <div className="LandingPage">
      <div className="LandingPage__hero">
        <Header noBorder={true} variation="dark" />
        <HeroInfo state={state} flags={flags} />
      </div>

      <div className="LandingPage__card-container">
        <div
          className={`LandingPage__card${
            mctYpOeNextYear ? " LandingPage__card--one-col" : ""
          }`}
        >
          <div className={mctYpOeNextYear ? "" : "LandingPage__card--col"}>
            <h3 className="LandingPage__cardHeader">
              {t("ooe_landing.new_to_medicare")}
            </h3>

            <p className="mct-p2">{t("ooe_landing.learn_about_options")}</p>

            <a
              href={`#${routes.coverageWizard.landingPage}`}
              className="ds-c-button ds-c-button--primary ds-u-padding-x--0"
              onClick={(): void => {
                // istanbul ignore next: tested elsewhere
                sendTealiumEvent(
                  "Find Plans Landing Page - Login",
                  "New to Medicare Continue"
                );
              }}
            >
              {t("landing_page.learn_about_options_button")}
            </a>
          </div>

          {!mctYpOeNextYear && (
            <div className="LandingPage__card--col">
              <h3 className="LandingPage__cardHeader">
                {t("ooe_landing.qualify")}
              </h3>

              <p className="mct-p2">
                <FormattedMessage
                  id="ooe_landing.change_coverage"
                  tagName="span"
                  values={{ year: mctCurrentPlanYear }}
                />
              </p>

              {state.beneficiary ? (
                <Link
                  to={beneRouteForThisYear}
                  className="ds-c-button ds-c-button--primary ds-u-margin-bottom--2 ds-u-padding-x--0"
                >
                  {t("landing_page.shop_for_plans")}
                </Link>
              ) : (
                <LoginLogoutButton
                  loginKey="ooe_landing.create_or_login"
                  className="ds-c-button--primary ds-u-margin-bottom--2"
                  onLogin={(): void => {
                    // istanbul ignore next: tested elsewhere
                    sendTealiumEvent(
                      "Find Plans Landing Page - Login",
                      "Special Enrollment Continue"
                    );
                    dispatch({
                      type: ActionType.UPDATE_YEAR,
                      payload: mctCurrentPlanYear.toString(),
                    });

                    initiateLogin(state.language, "", undefined);
                  }}
                  hideIfDisabled={true}
                />
              )}

              <br />

              {!state.beneficiary && (
                <Link
                  to={questionsRouteForThisYear}
                  className="LandingPage__cardLink e2e-sep-continue"
                  onClick={(): void => {
                    // istanbul ignore next: tested elsewhere
                    sendTealiumEvent(
                      "Find Plans Landing Page - Login",
                      "Continue without logging in 2"
                    );
                  }}
                >
                  {t("ooe_landing.continue_without_login")}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="LandingPage__bottomLinks">
        <HelpDrawerToggle
          helpDrawerOpen={showMedigapHelp}
          showDrawer={(): void => {
            setShowMedigapHelp(true);
            sendTealiumEvent("Help Drawer", "Looking for Medigap Plans");
          }}
          className="LandingPage__medigapLink"
          inline
        >
          {t("ooe_landing.looking_medigap")}
        </HelpDrawerToggle>

        {showMedigapHelp && (
          <LandingPageMedigapHelpDrawer
            setShowMedigapHelp={setShowMedigapHelp}
          />
        )}

        <div className="LandingPage__paceLink">
          <HashLink
            to={`${routes.pace.landingPage}${location.search}`}
            onClick={() => {
              const url = window.location.href.split("/?")[0];
              createGa4Event({
                event_name: Ga4Event.PACE_PLANS_CTA_CLICKED,
                content_type: "mct page",
                text: t("ooe_landing.pace_plans"),
                link_url: `${url}${routes.pace.landingPage}${location.search}`,
                site_section: "mct",
                state,
                flags,
              });
            }}
          >
            {t("ooe_landing.pace_plans")}
          </HashLink>
        </div>
      </div>
    </div>
  );
};

export default withRouter(LandingPage);
